import { compareDesc } from 'date-fns'

import { getUrlParams } from '@/utils/browser'
import { SORT, SORT_DEVS } from '@/constants'

const API_PACKAGE_APP_IDS_ORDERED = [
  process.env.GATSBY_PRO_PLUS_API_PACKAGE_APP_ID,
  process.env.GATSBY_PRO_API_PACKAGE_APP_ID,
  process.env.GATSBY_ESSENTIAL_API_PACKAGE_APP_ID,
].filter(Boolean)

const getApiPackageRank = (id) => {
  const index = API_PACKAGE_APP_IDS_ORDERED.indexOf(id)
  return index === -1 ? Infinity : index
}

const withApiPackagesFirst = (sortFn) => (a, b) => {
  const aRank = getApiPackageRank(a.id)
  const bRank = getApiPackageRank(b.id)

  if (aRank !== bRank) return aRank - bRank
  return sortFn(a, b)
}

export const isSupportedSortOption = (sort, options) =>
  !!options.find(({ VALUE }) => VALUE === sort)

export const getInitialSortValue = (defaultSortValue, sortOptions) => {
  const { sortBy } = getUrlParams()

  if (!sortBy) {
    return defaultSortValue || ''
  }

  if (sortOptions?.length && !isSupportedSortOption(sortBy, sortOptions)) {
    return defaultSortValue || ''
  }

  return sortBy
}

export const sortByPopularity = (a, b) => b.installations - a.installations

export const sortByPublishedDate = (a, b) =>
  compareDesc(a.publishedAt, b.publishedAt)

export const sortAlphabetically =
  (property = 'name') =>
  (a, b) => {
    let aValue = a[property] || a
    let bValue = b[property] || b

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toUpperCase().trim()
      bValue = bValue.toUpperCase().trim()
    }

    if (aValue < bValue) {
      return -1
    }
    if (aValue > bValue) {
      return 1
    }
    return 0
  }

export const sortByOrder = (a, b) => {
  const orderA = a.customProps?.order ? parseInt(a.customProps.order, 10) : 0
  const orderB = b.customProps?.order ? parseInt(b.customProps.order, 10) : 0

  return orderB - orderA
}

export const sortByOrderAndInstallations = (a, b) => {
  const orderA = a.customProps?.order ? parseInt(a.customProps.order, 10) : 0
  const orderB = b.customProps?.order ? parseInt(b.customProps.order, 10) : 0

  return orderB - orderA || b.installations - a.installations
}

export const sortByPaymentAndInstallations = (a, b) => {
  const aPrice = a?.payment?.price || 0
  const bPrice = b?.payment?.price || 0

  return bPrice - aPrice || b.installations - a.installations
}

export const sortDevsByProductsNumber = (a, b) => {
  const aProducts = a.services.length + a.apps.length
  const bProducts = b.services.length + b.apps.length
  return bProducts - aProducts
}

export const getSortFunction = (sort, sortByProperty) => {
  switch (sort) {
    case SORT.ALPHABETICALLY.VALUE:
      return sortAlphabetically(sortByProperty)

    case SORT.PRICE.VALUE:
      return sortByPaymentAndInstallations

    case SORT.POPULARITY.VALUE:
      return sortByPopularity

    case SORT.RECOMMENDED.VALUE:
      return withApiPackagesFirst(sortByOrderAndInstallations)

    case SORT.PUBLISHED.VALUE:
      return sortByPublishedDate

    case SORT_DEVS.PRODUCTS_NUMBER.VALUE:
      return sortDevsByProductsNumber

    case SORT_DEVS.DEVELOPER_NAME.VALUE:
      return sortAlphabetically('company')

    default:
      return null
  }
}

export const sortOn = (property, orderList) => {
  const order = orderList.reduce(
    (obj, key, index) => ({
      ...obj,
      [key]: index + 1,
    }),
    {}
  )
  const getVal = (item) => order[item[property]] || Infinity

  return (a, b) => getVal(a) - getVal(b)
}
